<template>
  <section class="what-we-do-section">
    <div class="section-container">

      <!-- 标题区域 -->
      <div class="header">
        <div class="tag">CORE CAPABILITIES</div>
        <h2 class="title">What We Do Best</h2>

        <!-- 居中分割线 -->
        <div class="divider-container">
          <div class="line"></div>
          <span class="square-icon">[■]</span>
          <div class="line"></div>
        </div>
      </div>

      <!-- 三大核心功能块 -->
      <div v-for="(feature, index) in features" :key="index" class="feature-block"
        :class="{ 'reverse': feature.isReverse }">

        <!-- 左侧：终端模拟器 -->
        <div class="terminal-panel">
          <div class="panel-header">
            <div class="dots">
              <span class="dot red"></span>
              <span class="dot yellow"></span>
              <span class="dot green"></span>
            </div>
            <span class="panel-title">{{ feature.terminalTitle }}</span>
          </div>

          <div class="terminal-body">
            <p v-for="(line, idx) in feature.terminalLines" :key="idx">
              <span :class="line.type">{{ line.text }}</span>
            </p>
            <span class="cursor">▊</span>
          </div>

          <!-- 底部统计数字 -->
          <div class="terminal-stats">
            <div class="t-stat">
              <div class="t-num">{{ feature.stat1.num }}</div>
              <div class="t-label">{{ feature.stat1.label }}</div>
            </div>
            <div class="t-stat">
              <div class="t-num">{{ feature.stat2.num }}</div>
              <div class="t-label">{{ feature.stat2.label }}</div>
            </div>
          </div>
        </div>

        <!-- 右侧：文本内容区 -->
        <div class="content-area">
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="description">{{ feature.desc }}</p>

          <ul class="feature-list">
            <li v-for="(item, i) in feature.list" :key="i">{{ item }}</li>
          </ul>

          <button class="btn-action">{{ feature.btnText }}</button>
        </div>

      </div>

    </div>
  </section>
</template>

<script>
export default {
  name: 'WhatWeDoBest',
  data() {
    return {
      features: [
        {
          title: 'Advanced Blockchain Forensics',
          desc: 'Our forensic analysts use industry-leading blockchain intelligence platforms to trace stolen assets across every major network. From Bitcoin and Ethereum to BNB Chain, Tron, Solana, and beyond — we follow the money wherever it goes.',
          list: [
            'Multi-chain transaction tracing across 18+ blockchain networks',
            'Wallet clustering and entity identification using graph analysis',
            'Exchange off-ramp detection and cross-chain bridge tracking',
            'Legal-grade evidence packages accepted by enforcement agencies',
            'Real-time monitoring alerts when flagged wallets become active'
          ],
          btnText: 'START TRACE',
          terminalTitle: 'BLOCKCHAIN_TRACE.exe',
          terminalLines: [
            { text: '$ trace_wallet 0x1f2...9f1a', type: 'cmd' },
            { text: '> Wallet flagged - HIGH RISK', type: 'warn' },
            { text: '$ map_transactions --depth 8', type: 'cmd' },
            { text: '> Tracing 47 linked wallets', type: 'out' },
            { text: '$ cross_chain_scan --all', type: 'cmd' },
            { text: '> Found on ETH -> TRX -> BNB', type: 'out' },
            { text: '$ compile_evidence', type: 'cmd' },
            { text: '> Evidence compiled.', type: 'out' }
          ],
          stat1: { num: '18+', label: 'CHAINS' },
          stat2: { num: '47', label: 'WALLETS TRACED' },
          isReverse: false
        },
        {
          title: 'Global Legal Enforcement',
          desc: 'Tracing the funds is only half the battle. Getting them back requires legal action — and that requires contacts, expertise, and a deep understanding of international financial crime law. We have all three.',
          list: [
            'Direct relationships with cybercrime units in 40+ countries',
            'Exchange cooperation requests for account freezing and fund holds',
            'Regulatory complaint filing with financial authorities worldwide',
            'Legal brief preparation for civil and criminal proceedings',
            'Ongoing case coordination until closure and fund return'
          ],
          btnText: 'START CASE',
          terminalTitle: 'LEGAL_ENFORCEMENT.exe',
          terminalLines: [
            { text: '$ init_enforcement_request', type: 'cmd' },
            { text: '> 14 enforcement contacts active', type: 'out' },
            { text: '$ submit_evidence --case #887', type: 'cmd' },
            { text: '> Evidence accepted by 3 agencies', type: 'out' },
            { text: '$ freeze_request --exchange binance', type: 'cmd' },
            { text: '> Account frozen PENDING', type: 'warn' },
            { text: '$ status_update', type: 'cmd' },
            { text: '> Status: Active', type: 'out' }
          ],
          stat1: { num: '40+', label: 'COUNTRIES' },
          stat2: { num: '14', label: 'AGENT CONTACTS' },
          isReverse: true
        },
        {
          title: 'Secure Case Management',
          desc: 'Every case at Fraud Counsel Department is handled with the highest level of security, discretion, and transparency. You get a dedicated case manager, encrypted communications, and regular updates throughout the entire process.',
          list: [
            'Dedicated case manager assigned from first contact to closure',
            '256-bit encrypted communications and data storage',
            'Regular case updates keeping you informed at every stage',
            'Full confidentiality — your case is never disclosed to third parties',
            'Comprehensive final report delivered upon case closure'
          ],
          btnText: 'OPEN CASE',
          terminalTitle: 'CASE_MANAGEMENT.exe',
          terminalLines: [
            { text: '$ encrypt_case_data --AES256', type: 'cmd' },
            { text: '> Case data fully encrypted', type: 'out' },
            { text: '$ assign_case_manager', type: 'cmd' },
            { text: '> Dedicated manager assigned', type: 'out' },
            { text: '$ schedule_update --24h', type: 'cmd' },
            { text: '> Client update scheduled daily', type: 'out' },
            { text: '$ mark_confidential', type: 'cmd' },
            { text: '> Marked confidential.', type: 'out' }
          ],
          stat1: { num: '256', label: 'BIT ENCRYPTION' },
          stat2: { num: '24H', label: 'UPDATE CYCLE' },
          isReverse: false
        }
      ]
    };
  }
}
</script>

<style scoped>
.what-we-do-section {
  background-color: #070b14;
  padding: 60px 5%;
  color: #e2e8f0;
}

.section-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* 标题区域 */
.header {
  position: relative;
  margin-bottom: 60px;
}

.tag {
  color: #4fd1c5;
  font-size: 0.75rem;
  letter-spacing: 2px;
  margin-bottom: 10px;
  font-weight: 500;
}

.title {
  font-size: 2.6rem;
  font-weight: 700;
  margin: 0 0 20px;
}

.divider-container {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 10px;
}

.divider-container .line {
  flex: 1;
  height: 1px;
  background-color: rgba(79, 209, 197, 0.2);
}

.divider-container .square-icon {
  color: #4fd1c5;
  font-size: 0.8rem;
  font-family: monospace;
  margin: 0 15px;
}

/* 功能块布局 */
.feature-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
  margin-bottom: 100px;
}

.feature-block.reverse {
  flex-direction: row-reverse;
}

/* 终端与文本区占比 */
.terminal-panel,
.content-area {
  flex: 1;
}

/* 终端模拟器样式 */
.terminal-panel {
  background: #0d1117;
  border: 1px solid #253045;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: #161b22;
  border-bottom: 1px solid #253045;
}

.dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot.red {
  background: #ff5f56;
}

.dot.yellow {
  background: #ffbd2e;
}

.dot.green {
  background: #27c93f;
}

.panel-title {
  color: #a0b3cc;
  font-family: monospace;
  font-size: 0.8rem;
  margin-left: 10px;
}

.terminal-body {
  padding: 15px;
  font-family: monospace;
  font-size: 0.8rem;
  line-height: 1.8;
  min-height: 180px;
}

.cmd {
  color: #4fd1c5;
}

.out {
  color: #a0b3cc;
}

.warn {
  color: #ffbd2e;
}

.cursor {
  color: #4fd1c5;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.terminal-stats {
  display: flex;
  justify-content: space-around;
  background: #0a101f;
  padding: 15px;
  border-top: 1px solid #253045;
}

.t-stat {
  text-align: center;
}

.t-num {
  color: #4fd1c5;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 5px;
}

.t-label {
  color: #a0b3cc;
  font-size: 0.7rem;
  letter-spacing: 1px;
}

/* 右侧内容区 */
.content-area {
  padding: 20px 0;
}

.feature-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #fff;
}

.description {
  color: #a0b3cc;
  font-size: 0.95rem;
  line-height: 1.7;
  margin-bottom: 25px;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
}

.feature-list li {
  position: relative;
  color: #cbd5e1;
  font-size: 0.9rem;
  margin-bottom: 12px;
  padding-left: 20px;
  line-height: 1.6;
}

.feature-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  background-color: #4fd1c5;
  border-radius: 50%;
}

/* 按钮样式 */
.btn-action {
  background: transparent;
  border: 1px solid #4fd1c5;
  color: #4fd1c5;
  padding: 12px 25px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-action:hover {
  background: rgba(79, 209, 197, 0.1);
}

/* 响应式适配 */
@media (max-width: 900px) {

  .feature-block,
  .feature-block.reverse {
    flex-direction: column;
    gap: 30px;
    margin-bottom: 60px;
  }

  .content-area {
    width: 100%;
  }

  .terminal-panel {
    width: 100%;
  }

  .title {
    font-size: 2rem;
  }
}
</style>